// Действията под прозореца „История на операциите": сторниране на цялата
// сметка, възстановяване, фактуриране и дубликат на бон.
//
// Изнесени от `BillOperationsModal.tsx` при редизайна — прозорецът показва,
// хукът решава кой бутон е активен и какво се случва при натискане. Логиката
// и текстовете са пренесени БЕЗ промяна; сменен е само файлът, в който живеят.

import { useState } from "react";
import { App } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import {
  useCancelAllBillItems,
  usePrintReceiptCopy,
  useRestoreBill,
} from "../../../../hooks/useBills";
import { usePrinterNotifications } from "../../../../hooks/usePrinterNotifications";
import type { BillHistory } from "../../../../api/reportsBillsHistory";
import { isCashNoReceiptBill } from "../../../../lib/billFiscalization";
import type { OpSummary } from "../../../../api/billOperations";
import type { Invoice } from "../../../../api/invoices";

/** Прозорецът за фактуриране — синхронен с `InvoiceBillPickerModal`. */
const INVOICE_WINDOW_DAYS = 5;

type Params = {
  bill: BillHistory | null;
  /** Липсва, докато операциите се зареждат — тогава сторното е недостъпно. */
  summary: OpSummary | undefined;
  onClose: () => void;
  onBillUpdated?: () => void;
};

export const useBillOperationsActions = ({
  bill,
  summary,
  onClose,
  onBillUpdated,
}: Params) => {
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();

  const cancelAllMutation = useCancelAllBillItems();
  const restoreAllMutation = useRestoreBill();
  const receiptCopyMutation = usePrintReceiptCopy();

  const [billSelectOpen, setBillSelectOpen] = useState(false);
  const [selectedBills, setSelectedBills] = useState<BillHistory[]>([]);
  const [invoiceCreateOpen, setInvoiceCreateOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);

  const handleCancelAll = () => {
    if (!bill) return;
    modal.confirm({
      title: "Сторниране на цялата сметка",
      icon: <ExclamationCircleOutlined className="!text-brand-ink" />,
      content: "Сигурни ли сте, че искате да сторнирате всички продукти?",
      okText: "Потвърди",
      cancelText: "Отказ",
      centered: true,
      okButtonProps: { style: { background: "#BD8A2C", borderColor: "#BD8A2C" } },
      onOk: async () => {
        try {
          const result = await cancelAllMutation.mutateAsync({
            billId: bill.id,
            data: { reason: "Сторниране на цяла сметка", isDefective: false },
          });
          onBillUpdated?.();
          onClose();
          const fiscal = result?.fiscal_result;
          if (!fiscal || fiscal.status === "success") message.success("Готово");
          else if (fiscal.status === "timeout")
            notifyPrinterTimeout(fiscal.error || "Принтерът не отговори навреме");
          else notifyPrinterError(fiscal.error || "Принтерът върна грешка");
        } catch (err) {
          const response = (
            err as { response?: { status?: number; data?: { detail?: string } } }
          )?.response;
          // 502 = принтерът отказа сторно бона и BE върна всичко назад —
          // нищо не е сторнирано; грешката стои до ръчно затваряне.
          if (response?.status === 502) {
            notifyPrinterError(response.data?.detail || "Принтерът върна грешка");
            return;
          }
          message.error(response?.data?.detail || "Грешка при сторниране");
        }
      },
    });
  };

  const runRestore = async (allowDuplicateName: boolean) => {
    if (!bill) return;
    try {
      const result = await restoreAllMutation.mutateAsync({
        billId: bill.id,
        data: {
          reason: "Възстановяване на сметка",
          isDefective: false,
          allowDuplicateName,
        },
      });
      onBillUpdated?.();
      onClose();
      // Възстановяването издава сторно бон за старото приключване — изходът
      // от принтера се показва като при „Сторнирай всичко", вместо да се
      // губи зад общото „Готово".
      const fiscal = result?.fiscal_result;
      if (!fiscal || fiscal.status === "success") message.success("Готово");
      else if (fiscal.status === "timeout")
        notifyPrinterTimeout(fiscal.error || "Принтерът не отговори навреме");
      else notifyPrinterError(fiscal.error || "Принтерът върна грешка");
    } catch (err) {
      const response = (
        err as { response?: { status?: number; data?: { detail?: string } } }
      )?.response;
      // 409 = вече има отворена маса със същото име. Питаме втори път, вместо
      // да отказваме — операторът решава дали да я дублира.
      if (response?.status === 409 && !allowDuplicateName) {
        confirmDuplicateName();
        return;
      }
      // 502 = принтерът отказа сторно бона — BE не е възстановил нищо.
      if (response?.status === 502) {
        notifyPrinterError(response?.data?.detail || "Принтерът върна грешка");
        return;
      }
      message.error(response?.data?.detail || "Грешка при възстановяване");
    }
  };

  const confirmDuplicateName = () => {
    if (!bill) return;
    modal.confirm({
      title: `Вече има отворена маса „${bill.name}“`,
      icon: <ExclamationCircleOutlined className="!text-brand-ink" />,
      content: `Ако продължите, ще има две отворени маси с име „${bill.name}“.`,
      okText: "Възстанови",
      cancelText: "Отказ",
      centered: true,
      okButtonProps: { style: { background: "#BD8A2C", borderColor: "#BD8A2C" } },
      onOk: () => runRestore(true),
    });
  };

  const handleRestoreAll = () => {
    modal.confirm({
      title: "Възстановяване на сметка",
      icon: <ExclamationCircleOutlined className="!text-brand-ink" />,
      content: "Сигурни ли сте, че искате да възстановите сметката?",
      okText: "Потвърди",
      cancelText: "Отказ",
      centered: true,
      okButtonProps: { style: { background: "#BD8A2C", borderColor: "#BD8A2C" } },
      onOk: () => runRestore(false),
    });
  };

  const daysSinceClosed = bill?.closed_at
    ? dayjs().startOf("day").diff(dayjs(bill.closed_at).startOf("day"), "day")
    : Infinity;
  // Закритата в брой без бон („Закриване на сметка(1)") няма фискален
  // документ, срещу който да се издаде фактура — бутонът стои сив.
  const isCashNoReceipt = !!bill && isCashNoReceiptBill(bill);
  const canInvoice =
    !!bill &&
    bill.status === "closed" &&
    !bill.is_invoiced &&
    !isCashNoReceipt &&
    daysSinceClosed <= INVOICE_WINDOW_DAYS;

  const handleInvoice = () => {
    if (!bill) return;
    if (!canInvoice) {
      if (bill.is_invoiced) message.warning(t("invoicing.warnAlreadyInvoiced"));
      else if (bill.status !== "closed")
        message.warning(t("invoicing.warnOnlyClosed"));
      else if (isCashNoReceipt)
        message.warning(t("invoicing.warnCashNoReceipt"));
      else
        message.warning(
          t("invoicing.warnWindowExpired", { days: INVOICE_WINDOW_DAYS }),
        );
      return;
    }
    // Стъпка 1 — избор на сметка/и. Текущата е пред-избрана, но операторът
    // може да добави и други нефактурирани сметки от последните дни.
    setBillSelectOpen(true);
  };

  const activeAmount = summary
    ? Number(summary.gross_revenue) + Number(summary.cancelled_amount)
    : 0;
  // „Сторнирай всичко" — има активни (несторнирани) редове, които да се отменят.
  const canCancel = activeAmount > 0;
  // „Възстанови" — всяка затворена сметка с активни редове. Възстановяването
  // отваря СЪЩАТА сметка наново (същото id, `service_restore.py`): бонът ѝ се
  // сторнира, редовете остават, старото плащане отива в хронологията. Броят
  // сторнирани редове няма нищо общо с това дали е възможно — с предишното
  // условие `cancelled_count > 0` бутонът стоеше сив точно за сметките, които
  // най-често се възстановяват. Тук няма и времеви прозорец, за разлика от
  // списъка в „Маркиране" (последните 2 часа).
  const canRestore = bill?.status === "closed";

  // „Дубликат на бон" — копие от КЛЕН на вече издадения фискален бон.
  // Четирите условия са огледало на BE-то (`router_receipt_copy.py`),
  // което ги налага наистина; тук са, за да е ясно ПРЕДИ натискане кои
  // редове изобщо имат бон. Фискализирана значи „има номер И ненулева
  // сума" — затворена сметка с нулев тотал (плащане „по сметка") минава
  // без фискален документ и няма какво да се копира.
  const isFiscalised =
    !!(bill?.receipt_number || "").trim() && Number(bill?.total_amount) !== 0;
  const isClosedToday =
    !!bill?.closed_at && dayjs(bill.closed_at).isSame(dayjs(), "day");
  const canPrintReceiptCopy =
    bill?.status === "closed" && isFiscalised && isClosedToday;
  // Сивият бутон без обяснение е неразличим от счупен — затова причината
  // стои в tooltip-а.
  const receiptCopyHint = canPrintReceiptCopy
    ? `Копие от КЛЕН на бон № ${bill?.receipt_number}`
    : bill?.status !== "closed"
      ? "Копие се печата само на затворена сметка."
      : !isFiscalised
        ? "Сметката няма издаден фискален бон."
        : "Копие може да се извади само за сметка, затворена днес.";

  const handlePrintReceiptCopy = async () => {
    // Мутацията е и защитата от двойно натискане: бутонът е disabled,
    // докато заявката е в полет, а ранният изход хваща и второто кликане,
    // стигнало преди пре-рендера.
    if (!bill || receiptCopyMutation.isPending) return;
    try {
      await receiptCopyMutation.mutateAsync({ billId: bill.id });
      message.success("Копието е изпратено за печат");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      notifyPrinterError(detail || "Грешка при печат на копието");
    }
  };

  return {
    canCancel,
    canRestore: !!canRestore,
    canInvoice,
    canPrintReceiptCopy: !!canPrintReceiptCopy,
    receiptCopyHint,
    cancelLoading: cancelAllMutation.isPending,
    restoreLoading: restoreAllMutation.isPending,
    receiptCopyLoading: receiptCopyMutation.isPending,
    handleCancelAll,
    handleRestoreAll,
    handleInvoice,
    handlePrintReceiptCopy,
    invoiceFlow: {
      billSelectOpen,
      closeBillSelect: () => setBillSelectOpen(false),
      confirmBillSelect: (bills: BillHistory[]) => {
        setSelectedBills(bills);
        setBillSelectOpen(false);
        setInvoiceCreateOpen(true);
      },
      selectedBills,
      invoiceCreateOpen,
      closeInvoiceCreate: () => setInvoiceCreateOpen(false),
      onInvoiceSaved: (invoice: Invoice) => {
        setInvoiceCreateOpen(false);
        setPreviewInvoice(invoice);
      },
      previewInvoice,
      closePreview: () => setPreviewInvoice(null),
    },
  };
};
